import { at, num, f, record, put, isArray } from './support-b1';
import { empty, phpCopy } from '../../php-compat';
import type { PortEnv } from '../../port-contract';
interface Deps {
    ft3d_minimap_position(row: unknown): unknown;
    ft_shape_floor_band_for_position(shape: unknown, x: unknown, y: unknown, z: unknown): unknown;
    encounter_field(e: unknown, key: string): unknown;
    ft3d_minimap_sensed_by_viewer(e: unknown, shape: unknown, detector: unknown, target: unknown, light: unknown, dark: unknown, obscured: unknown): boolean;
    has_status_effect(row: unknown, id: unknown): boolean;
    'StatusEffect::getId'(key: string): unknown;
    ft_shape_is_derived_exterior_fallback(shape: unknown): boolean;
    ft_shape_segment_hit_3d(shape: unknown, from: unknown, to: unknown, mode: string): unknown;
    ft_combat_shape_hit_is_closed_door(shape: unknown, hit: unknown): boolean;
}
/** PHP: ft_combat_hearing_range_ft (includes/functions/janus/encounter/combat_membership.php:166) */
export function ftCombatHearingRangeFt(door: boolean, radius: unknown = null): unknown {
    radius = radius === null ? null : f(num(radius));
    return f(num(radius ?? 30) * (door ? .5 : 1));
}
/** PHP: ft_combat_detection_channel (includes/functions/janus/encounter/combat_membership.php:178) */
export function ftCombatDetectionChannel(see: boolean, distance: unknown, deaf: boolean, door = false, radius: unknown = null): string | null {
    distance = f(num(distance));
    if (see)
        return 'sight';
    if (deaf || num(distance) < 0)
        return null;
    return num(distance) <= num(ftCombatHearingRangeFt(door, radius)) ? 'hearing' : null;
}
/** PHP: ft_combat_world_position (includes/functions/janus/encounter/combat_membership.php:192) */
export function ftCombatWorldPosition(row: unknown, env: PortEnv<Deps>): unknown { const p = env.deps.ft3d_minimap_position(row); return record({ x: at(p, 'x'), y: at(p, 'y') ?? f(0), z: at(p, 'y') === null ? f(0) : at(p, 'z'), depth_authored: at(p, 'y') !== null }); }
/** PHP: ft_combat_distance_ft (includes/functions/janus/encounter/combat_membership.php:203) */
export function ftCombatDistanceFt(a: unknown, b: unknown, env: PortEnv<Deps>): unknown { const pa = env.deps.ft3d_minimap_position(a), pb = env.deps.ft3d_minimap_position(b), dx = num(at(pa, 'x')) - num(at(pb, 'x')), dy = num(at(pa, 'y') ?? 0) - num(at(pb, 'y') ?? 0), dz = num(at(pa, 'z')) - num(at(pb, 'z')); return f(Math.sqrt(dx * dx + dy * dy + dz * dz)); }
/** PHP: ft_combat_floor_band (includes/functions/janus/encounter/combat_membership.php:214) */
export function ftCombatFloorBand(shape: unknown, row: unknown, env: PortEnv<Deps>): unknown { const p = env.deps.ft3d_minimap_position(row); return env.deps.ft_shape_floor_band_for_position(shape, at(p, 'x'), at(p, 'y') ?? f(0), at(p, 'z')); }
/** PHP: ft_combat_rows_share_floor (includes/functions/janus/encounter/combat_membership.php:225) */
export function ftCombatRowsShareFloor(shape: unknown, a: unknown, b: unknown, env: PortEnv<Deps>): boolean { return Math.abs(num(ftCombatFloorBand(shape, a, env)) - num(ftCombatFloorBand(shape, b, env))) <= .01; }
/** PHP: ft_combat_sound_path (includes/functions/janus/encounter/combat_membership.php:318) */
export function ftCombatSoundPath(shape: unknown, a: unknown, b: unknown, env: PortEnv<Deps>): string {
    const pa = ftCombatWorldPosition(a, env), pb = ftCombatWorldPosition(b, env);
    if (empty(at(pa, 'depth_authored')) || empty(at(pb, 'depth_authored')) || empty(at(shape, 'boxes')) || env.deps.ft_shape_is_derived_exterior_fallback(shape))
        return 'open';
    let source = record({ x: at(pa, 'x'), y: at(pa, 'y'), z: f(num(at(pa, 'z')) + 2.5) });
    const dest = record({ x: at(pb, 'x'), y: at(pb, 'y'), z: f(num(at(pb, 'z')) + 2.5) });
    let door = false;
    for (let guard = 0; guard < 8; guard++) {
        const hit = env.deps.ft_shape_segment_hit_3d(shape, source, dest, 'sight');
        if (hit === null)
            return door ? 'door' : 'open';
        if (!env.deps.ft_combat_shape_hit_is_closed_door(shape, hit))
            return 'blocked';
        door = true;
        const length = Math.hypot(num(at(dest, 'x')) - num(at(source, 'x')), num(at(dest, 'y')) - num(at(source, 'y'))), step = Math.min(1, num(at(hit, 't')) + .05 / Math.max(.05, length));
        if (step >= 1)
            return 'door';
        source = record(Object.fromEntries(['x', 'y', 'z'].map(axis => [axis, f(num(at(source, axis)) + (num(at(dest, axis)) - num(at(source, axis))) * step)])));
    }
    return 'blocked';
}
/** PHP: ft_combat_detection_between (includes/functions/janus/encounter/combat_membership.php:261) */
export function ftCombatDetectionBetween(e: unknown, shape: unknown, detector: unknown, target: unknown, light: unknown, dark: unknown, obscured: unknown, radius: unknown = null, env: PortEnv<Deps>): string | null {
    radius = radius === null ? null : f(num(radius));
    detector = phpCopy(detector);
    target = phpCopy(target);
    if (env.deps.encounter_field(e, 'encounter_shape') !== null) {
        if (at(env.deps.ft3d_minimap_position(detector), 'y') === null)
            put(detector, ['position', 'y_pos'], f(0));
        if (at(env.deps.ft3d_minimap_position(target), 'y') === null)
            put(target, ['position', 'y_pos'], f(0));
    }
    if (!ftCombatRowsShareFloor(shape, detector, target, env))
        return null;
    if (env.deps.ft3d_minimap_sensed_by_viewer(e, shape, detector, target, light, dark, obscured))
        return 'sight';
    const distance = ftCombatDistanceFt(detector, target, env), deaf = env.deps.has_status_effect(detector, env.deps['StatusEffect::getId']('deafened'));
    if (deaf || num(distance) > num(radius ?? 30))
        return null;
    const path = ftCombatSoundPath(shape, detector, target, env);
    return path === 'blocked' ? null : ftCombatDetectionChannel(false, distance, false, path === 'door', radius);
}
